/** Client-side crypto for ucomm file-send (Send-like; not ffsend-compatible). */

const TEXT = new TextEncoder();
const RECORD_PLAINTEXT_BYTES = 1024 * 1024; // 1 MiB
const IV_BYTES = 12;
const GCM_TAG_BYTES = 16;
const MAGIC = TEXT.encode('UCFS');
const VERSION = 1;

export type DerivedKeys = {
  fileKey: CryptoKey;
  metaKey: CryptoKey;
  authKeyRaw: Uint8Array;
};

export type FileMeta = {
  filename: string;
  mime: string;
  size: number;
};

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bufferToBase64(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin);
}

export function base64ToBuffer(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function generateSecret(): { secret: Uint8Array; secretFragment: string } {
  const secret = crypto.getRandomValues(new Uint8Array(16));
  return { secret, secretFragment: toBase64Url(secret) };
}

export function parseSecretFragment(fragment: string): Uint8Array {
  const cleaned = fragment.replace(/^#/, '').trim();
  if (!cleaned) throw new Error('Missing encryption secret in link');
  const secret = fromBase64Url(cleaned);
  if (secret.length < 16) throw new Error('Invalid encryption secret');
  return secret;
}

async function hkdfBits(secret: Uint8Array, info: string, length: number): Promise<Uint8Array> {
  const baseKey = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: TEXT.encode(info),
    },
    baseKey,
    length * 8,
  );
  return new Uint8Array(bits);
}

export async function deriveKeys(secret: Uint8Array): Promise<DerivedKeys> {
  const fileRaw = await hkdfBits(secret, 'ucomm-file-send-file-v1', 32);
  const metaRaw = await hkdfBits(secret, 'ucomm-file-send-meta-v1', 32);
  const authKeyRaw = await hkdfBits(secret, 'ucomm-file-send-auth-v1', 32);

  const fileKey = await crypto.subtle.importKey('raw', fileRaw, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
  const metaKey = await crypto.subtle.importKey('raw', metaRaw, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
  return { fileKey, metaKey, authKeyRaw };
}

export async function encryptMetadata(metaKey: CryptoKey, meta: FileMeta): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = TEXT.encode(JSON.stringify(meta));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, metaKey, plaintext));
  const out = new Uint8Array(IV_BYTES + ct.length);
  out.set(iv, 0);
  out.set(ct, IV_BYTES);
  return out;
}

export async function decryptMetadata(metaKey: CryptoKey, blob: Uint8Array): Promise<FileMeta> {
  if (blob.length < IV_BYTES + GCM_TAG_BYTES + 1) throw new Error('Invalid metadata');
  const iv = blob.slice(0, IV_BYTES);
  const ct = blob.slice(IV_BYTES);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, metaKey, ct);
  const parsed = JSON.parse(new TextDecoder().decode(pt)) as FileMeta;
  if (!parsed?.filename || typeof parsed.size !== 'number') {
    throw new Error('Invalid metadata contents');
  }
  return parsed;
}

function writeHeader(totalPlaintextSize: number): Uint8Array {
  const header = new Uint8Array(4 + 1 + 4 + 8);
  header.set(MAGIC, 0);
  header[4] = VERSION;
  const view = new DataView(header.buffer);
  view.setUint32(5, RECORD_PLAINTEXT_BYTES, false);
  // uint64 big-endian size
  const high = Math.floor(totalPlaintextSize / 2 ** 32);
  const low = totalPlaintextSize >>> 0;
  view.setUint32(9, high, false);
  view.setUint32(13, low, false);
  return header;
}

function readHeader(buf: Uint8Array): { recordSize: number; totalSize: number; headerLen: number } {
  if (buf.length < 17) throw new Error('Truncated header');
  for (let i = 0; i < 4; i++) {
    if (buf[i] !== MAGIC[i]) throw new Error('Invalid file format');
  }
  if (buf[4] !== VERSION) throw new Error('Unsupported version');
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const recordSize = view.getUint32(5, false);
  const high = view.getUint32(9, false);
  const low = view.getUint32(13, false);
  const totalSize = high * 2 ** 32 + low;
  return { recordSize, totalSize, headerLen: 17 };
}

export type EncryptProgress = { bytesDone: number; bytesTotal: number };

/**
 * Encrypt a File into ciphertext Uint8Array chunks suitable for sequential upload.
 * Yields ciphertext pieces (header first, then each record).
 */
export async function* encryptFileToChunks(
  file: File,
  fileKey: CryptoKey,
  onProgress?: (p: EncryptProgress) => void,
): AsyncGenerator<Uint8Array> {
  yield writeHeader(file.size);
  let offset = 0;
  while (offset < file.size) {
    const end = Math.min(offset + RECORD_PLAINTEXT_BYTES, file.size);
    const slice = new Uint8Array(await file.slice(offset, end).arrayBuffer());
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ct = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, fileKey, slice),
    );
    const record = new Uint8Array(IV_BYTES + ct.length);
    record.set(iv, 0);
    record.set(ct, IV_BYTES);
    yield record;
    offset = end;
    onProgress?.({ bytesDone: offset, bytesTotal: file.size });
  }
}

export async function decryptCiphertextToBlob(
  ciphertext: Uint8Array,
  fileKey: CryptoKey,
  expectedMeta: FileMeta,
  onProgress?: (p: EncryptProgress) => void,
): Promise<Blob> {
  const { recordSize, totalSize, headerLen } = readHeader(ciphertext);
  if (totalSize !== expectedMeta.size) {
    throw new Error('Size mismatch');
  }
  const parts: Uint8Array[] = [];
  let offset = headerLen;
  let plainDone = 0;
  while (plainDone < totalSize) {
    const remaining = totalSize - plainDone;
    const plainLen = Math.min(recordSize, remaining);
    const recordLen = IV_BYTES + plainLen + GCM_TAG_BYTES;
    if (offset + recordLen > ciphertext.length) {
      throw new Error('Truncated ciphertext');
    }
    const iv = ciphertext.slice(offset, offset + IV_BYTES);
    const ct = ciphertext.slice(offset + IV_BYTES, offset + recordLen);
    const pt = new Uint8Array(
      await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, fileKey, ct),
    );
    if (pt.length !== plainLen) throw new Error('Record length mismatch');
    parts.push(pt);
    offset += recordLen;
    plainDone += plainLen;
    onProgress?.({ bytesDone: plainDone, bytesTotal: totalSize });
  }
  return new Blob(parts, { type: expectedMeta.mime || 'application/octet-stream' });
}

export async function signNonce(authKeyRaw: Uint8Array, nonce: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    authKeyRaw,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, TEXT.encode(nonce)));
  return Array.from(sig)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function buildShareUrl(origin: string, id: string, secretFragment: string): string {
  return `${origin.replace(/\/$/, '')}/s/${id}#${secretFragment}`;
}

/**
 * Derive a user-specific key for encrypting secrets at rest (cross-device sync).
 * Uses userId as salt - not the most secure but simple and works without password.
 */
async function deriveUserKey(userId: number): Promise<CryptoKey> {
  const salt = TEXT.encode(`ucomm-file-send-user-${userId}`);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    salt,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 10000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypt the URL fragment secret for storage in database (cross-device sync).
 */
export async function encryptSecretForUser(secretFragment: string, userId: number): Promise<string> {
  const userKey = await deriveUserKey(userId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = TEXT.encode(secretFragment);
  
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    userKey,
    plaintext,
  );
  
  // Return: iv (12 bytes) + ciphertext (base64)
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  
  return bufferToBase64(combined);
}

/**
 * Decrypt the URL fragment secret from database (cross-device sync).
 */
export async function decryptSecretForUser(encryptedSecret: string, userId: number): Promise<string> {
  const userKey = await deriveUserKey(userId);
  const combined = base64ToBuffer(encryptedSecret);
  
  if (combined.length < 13) throw new Error('Invalid encrypted secret');
  
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);
  
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    userKey,
    ciphertext,
  );
  
  const decoder = new TextDecoder();
  return decoder.decode(plaintext);
}

export { RECORD_PLAINTEXT_BYTES };
